import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";
import { DEFAULT_VIEW } from "./constants";
import {
  DraftUpdater,
  Resolution,
  WorkDraftMap,
  WorkDrafts,
  WorkFailure,
  WorkKind,
  WorkOrder,
  WorkQueueFilters,
  WorkQueueProviderProps,
  WorkQueueSession,
} from "./types";

const WorkQueueContext = createContext<WorkQueueSession | null>(null);

const NO_DRAFTS: WorkDrafts = { request: {}, instrument: {}, corporate: {} };

const withoutDrafts = (
  drafts: WorkDrafts,
  kind: WorkKind,
  keys: string[],
): WorkDrafts => {
  const remaining = { ...drafts[kind] };
  keys.forEach((key) => delete remaining[key]);
  return { ...drafts, [kind]: remaining };
};

const withUpdatedDraft = <K extends WorkKind>(
  drafts: WorkDrafts,
  kind: K,
  key: string,
  updater: DraftUpdater<WorkDraftMap[K]>,
): WorkDrafts => {
  const kindDrafts: Record<string, WorkDraftMap[K]> = drafts[kind];
  const next = updater(kindDrafts[key] ?? null);
  if (next !== null) {
    return { ...drafts, [kind]: { ...kindDrafts, [key]: next } };
  }
  return key in kindDrafts ? withoutDrafts(drafts, kind, [key]) : drafts;
};

// Lives above the routes so a page change keeps it, and inside the shell so logging out drops it.
export const WorkQueueProvider: React.FC<WorkQueueProviderProps> = ({
  children,
}) => {
  const [view, setView] = useState(DEFAULT_VIEW);
  const [drafts, setDrafts] = useState(NO_DRAFTS);
  const [resolved, setResolved] = useState<Resolution[]>([]);
  const [outcomeKey, setOutcomeKey] = useState<string | null>(null);
  const [failure, setFailure] = useState<WorkFailure | null>(null);

  const hasDrafts = Object.values(drafts).some(
    (kindDrafts) => Object.keys(kindDrafts).length > 0,
  );

  useUnsavedChangesGuard(hasDrafts);

  const changeFilters = useCallback((changes: Partial<WorkQueueFilters>) => {
    setOutcomeKey(null);
    setView((current) => ({ ...current, ...changes }));
  }, []);

  const changeOrder = useCallback(
    (order: WorkOrder) => setView((current) => ({ ...current, order })),
    [],
  );

  const updateDraft = useCallback(
    <K extends WorkKind>(
      kind: K,
      key: string,
      updater: DraftUpdater<WorkDraftMap[K]>,
    ) => setDrafts((current) => withUpdatedDraft(current, kind, key, updater)),
    [],
  );

  const updateDrafts = useCallback(
    <K extends WorkKind>(
      kind: K,
      keys: string[],
      updater: DraftUpdater<WorkDraftMap[K]>,
    ) =>
      setDrafts((current) =>
        keys.reduce(
          (drafts, key) => withUpdatedDraft(drafts, kind, key, updater),
          current,
        ),
      ),
    [],
  );

  const pruneDrafts = useCallback((kind: WorkKind, liveKeys: string[]) => {
    setDrafts((current) => {
      const staleKeys = Object.keys(current[kind]).filter(
        (key) => !liveKeys.includes(key),
      );
      return staleKeys.length > 0
        ? withoutDrafts(current, kind, staleKeys)
        : current;
    });
  }, []);

  const clearOutcome = useCallback(() => setOutcomeKey(null), []);

  const recordDecision = useCallback((resolution: Resolution) => {
    setResolved((current) => [resolution, ...current]);
    setOutcomeKey(resolution.key);
    setDrafts((current) =>
      withoutDrafts(current, resolution.kind, [resolution.key]),
    );
    setFailure(null);
  }, []);

  const dropResolution = useCallback((key: string) => {
    setResolved((current) =>
      current.filter((resolution) => resolution.key !== key),
    );
    setOutcomeKey((current) => (current === key ? null : current));
  }, []);

  const setDecisionWarning = useCallback(
    (key: string, warning: string) =>
      setResolved((current) =>
        current.map((resolution) =>
          resolution.kind === "request" && resolution.key === key
            ? { ...resolution, warning }
            : resolution,
        ),
      ),
    [],
  );

  const session = useMemo(
    () => ({
      view,
      drafts,
      resolved,
      outcomeKey,
      failure,
      changeFilters,
      changeOrder,
      updateDraft,
      updateDrafts,
      pruneDrafts,
      clearOutcome,
      recordDecision,
      dropResolution,
      setDecisionWarning,
      setFailure,
    }),
    [
      view,
      drafts,
      resolved,
      outcomeKey,
      failure,
      changeFilters,
      changeOrder,
      updateDraft,
      updateDrafts,
      pruneDrafts,
      clearOutcome,
      recordDecision,
      dropResolution,
      setDecisionWarning,
    ],
  );

  return (
    <WorkQueueContext.Provider value={session}>
      {children}
    </WorkQueueContext.Provider>
  );
};

export const useWorkQueueSession = (): WorkQueueSession => {
  const session = useContext(WorkQueueContext);
  if (!session) {
    throw new Error(
      "useWorkQueueSession must be used inside WorkQueueProvider",
    );
  }
  return session;
};
